import { useEffect, useState, useCallback, useRef } from "react";
import { PortalShell } from "@/components/Portal";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Baby, School, ClipboardList, CreditCard, ListPlus,
  CheckSquare, CalendarDays, CalendarX, Settings as SettingsIcon, Loader2, Plus,
  Trash2, Megaphone, PoundSterling, AlertTriangle, TrendingUp, Upload, Eye, Check, X,
  Archive, RotateCcw, UserPlus, ShieldCheck, ArrowRightLeft, Mail,
} from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "contacts", label: "Contacts", icon: Users },
  { key: "gymnasts", label: "Gymnasts", icon: Baby },
  { key: "classes", label: "Classes", icon: School },
  { key: "bookings", label: "Bookings", icon: ClipboardList },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "waiting", label: "Waiting Lists", icon: ListPlus },
  { key: "attendance", label: "Attendance", icon: CheckSquare },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "exclusions", label: "Exclusion Dates", icon: CalendarX },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

const STAFF_TABS = [
  { key: "contacts", label: "Contacts", icon: Users },
  { key: "attendance", label: "Attendance", icon: CheckSquare },
];

export default function AdminPortal() {
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const tabs = isStaff ? STAFF_TABS : TABS;
  const [tab, setTab] = useState(isStaff ? "contacts" : "dashboard");
  return (
    <PortalShell title={isStaff ? "Staff Portal" : "Admin Portal"} tabs={tabs} active={tab} onTab={setTab}>
      {tab === "dashboard" && !isStaff && <Dashboard onNav={setTab} />}
      {tab === "contacts" && <Contacts />}
      {tab === "gymnasts" && !isStaff && <Gymnasts />}
      {tab === "classes" && !isStaff && <Classes />}
      {tab === "bookings" && !isStaff && <Bookings />}
      {tab === "payments" && !isStaff && <Payments />}
      {tab === "waiting" && !isStaff && <Waiting />}
      {tab === "attendance" && <Attendance />}
      {tab === "calendar" && !isStaff && <CalendarView />}
      {tab === "exclusions" && !isStaff && <Exclusions />}
      {tab === "settings" && !isStaff && <SettingsSection />}
    </PortalShell>
  );
}

const Panel = ({ title, action, children, testid }) => (
  <div className="rounded-3xl bg-white border border-border p-6 mb-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-display text-2xl text-slate-800">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

const Loading = () => <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-primary" size={36} /></div>;

/* ---------------- PIN CONFIRM DIALOG ---------------- */
function PinConfirmDialog({ open, onOpenChange, title, description, onConfirm, confirmLabel = "Confirm", testid }) {
  const ADMIN_PIN = "2507";
  const [step, setStep] = useState(1);
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);

  const reset = () => { setStep(1); setPin(""); setError(false); };
  const handleOpenChange = (o) => { if (!o) reset(); onOpenChange(o); };

  const handleConfirm = () => {
    if (pin === ADMIN_PIN) { onConfirm(); reset(); }
    else { setError(true); setPin(""); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-destructive">{title}</DialogTitle>
        </DialogHeader>
        {step === 1 ? (
          <>
            <p className="text-sm text-slate-600">{description}</p>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => handleOpenChange(false)}>Cancel</Button>
              <Button variant="destructive" className="rounded-full" onClick={() => setStep(2)}>
                Yes, continue
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-1">Enter your 4-digit admin PIN to confirm:</p>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={4}
              className="rounded-xl text-center text-2xl tracking-[0.5em] font-mono"
              placeholder="••••"
              value={pin}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 4)); setError(false); }}
              onKeyDown={(e) => e.key === "Enter" && pin.length === 4 && handleConfirm()}
              autoFocus
              data-testid={testid ? `${testid}-pin` : "pin-confirm-input"}
            />
            {error && <p className="text-destructive text-sm text-center mt-1">Incorrect PIN. Try again.</p>}
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => { setStep(1); setPin(""); setError(false); }}>Back</Button>
              <Button variant="destructive" className="rounded-full" disabled={pin.length !== 4} onClick={handleConfirm} data-testid={testid}>
                {confirmLabel}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- DASHBOARD ---------------- */
function Dashboard({ onNav }) {
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [confirmDelAnn, setConfirmDelAnn] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addAnnouncement = async () => {
    if (!title || !body) return;
    try {
      await api.post("/admin/announcements", { title, body });
      setTitle(""); setBody(""); load();
      toast.success("Announcement posted");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/announcements/${id}`); load(); setConfirmDelAnn(null); };

  if (!stats) return <Loading />;
  const cards = [
    { label: "Active Gymnasts", value: stats.active_gymnasts, icon: Baby, color: "bg-primary" },
    { label: "Active Bookings", value: stats.active_bookings, icon: ClipboardList, color: "bg-secondary" },
    { label: "Classes Today", value: stats.classes_today, icon: School, color: "bg-accent text-accent-foreground" },
    { label: "Waiting List", value: stats.waiting_list, icon: ListPlus, color: "bg-slate-700" },
    { label: "Spaces Remaining", value: stats.spaces_remaining, icon: Users, color: "bg-secondary" },
    { label: "Expected Monthly Income", value: `£${stats.expected_monthly_income.toFixed(2)}`, icon: PoundSterling, color: "bg-success" },
    { label: "Failed Payments", value: stats.failed_payments, icon: AlertTriangle, color: "bg-destructive" },
    { label: "Outstanding Actions", value: stats.outstanding_actions, icon: TrendingUp, color: "bg-primary" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl text-primary mb-6 lg:hidden">Dashboard</h1>

      {stats.failed_payments > 0 && (
        <div className="mb-5 rounded-2xl bg-destructive/10 border border-destructive/30 p-4 flex items-center gap-3" data-testid="failed-payments-banner">
          <AlertTriangle className="text-destructive shrink-0" size={20} />
          <div className="flex-1 text-sm text-slate-700">
            <b className="text-destructive">{stats.failed_payments} failed payment{stats.failed_payments !== 1 ? "s" : ""} require attention.</b>
            {" "}Direct Debit collection was unsuccessful for one or more families.
          </div>
          <Button size="sm" variant="outline" className="rounded-full border-destructive text-destructive hover:bg-destructive hover:text-white shrink-0" onClick={() => onNav("payments")} data-testid="view-failures-btn">
            View Failures
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div
            key={c.label}
            className={`rounded-3xl p-5 text-white ${c.color} ${c.label === "Failed Payments" && stats.failed_payments > 0 ? "cursor-pointer ring-2 ring-destructive/50" : ""}`}
            data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={c.label === "Failed Payments" ? () => onNav("payments") : undefined}
          >
            <c.icon size={22} className="opacity-80" />
            <div className="text-2xl font-display mt-2">{c.value}</div>
            <div className="text-xs opacity-90">{c.label}</div>
          </div>
        ))}
      </div>

      <Panel title="Announcements" testid="admin-announcements">
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl" data-testid="ann-title" />
          <Input placeholder="Message" value={body} onChange={(e) => setBody(e.target.value)} className="rounded-xl sm:col-span-1" data-testid="ann-body" />
          <Button onClick={addAnnouncement} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="ann-add">
            <Plus size={16} className="mr-1" /> Post
          </Button>
        </div>
        {announcements.map((a) => (
          <div key={a.id} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
            <Megaphone className="text-primary shrink-0 mt-1" size={16} />
            <div className="flex-1">
              <div className="font-semibold text-slate-800">{a.title}</div>
              <div className="text-slate-600 text-sm">{a.body}</div>
            </div>
            <button onClick={() => setConfirmDelAnn(a.id)} className="text-slate-400 hover:text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
      </Panel>
      <PinConfirmDialog
        open={!!confirmDelAnn}
        onOpenChange={(o) => !o && setConfirmDelAnn(null)}
        title="Delete Announcement?"
        description="This will permanently remove the announcement from the portal."
        onConfirm={() => del(confirmDelAnn)}
        confirmLabel="Delete"
        testid="confirm-delete-ann"
      />
    </div>
  );
}

/* ---------------- CONTACTS ---------------- */
function Contacts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/contacts?q=${encodeURIComponent(q)}&show_archived=${showArchived}`).then((r) => setRows(r.data)).catch(() => {});
  }, [q, showArchived]);
  useEffect(() => { load(); }, [load]);

  const archive = async (r) => {
    try { await api.post(`/admin/contacts/${r.id}/archive`); toast.success("Contact archived"); setSelected(null); setConfirmArchive(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const unarchive = async (r) => {
    try { await api.post(`/admin/contacts/${r.id}/unarchive`); toast.success("Contact restored"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const permDelete = async (r) => {
    try { await api.delete(`/admin/contacts/${r.id}`); toast.success("Contact permanently deleted"); setSelected(null); setConfirmDelete(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-contacts">
      <Panel title="Contacts (CRM)" action={
        isAdmin && (
          <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} />
            Show archived
          </label>
        )
      }>
        <Input placeholder="Search parents or gymnasts..." value={q} onChange={(e) => setQ(e.target.value)}
          className="rounded-xl mb-4 max-w-md" data-testid="contacts-search" />
        {!rows ? <Loading /> : rows.length === 0 ? <p className="text-slate-500">No contacts found.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Parent</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead>
              <TableHead>Children</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`contact-row-${r.id}`} className={r.archived ? "opacity-50" : ""}>
                  <TableCell className="font-medium">{r.name}{r.archived && <Badge className="ml-2 rounded-full bg-slate-200 text-slate-500 text-xs">Archived</Badge>}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.phone || "—"}</TableCell>
                  <TableCell>{r.gymnasts.map((g) => `${g.first_name} ${g.last_name}`).join(", ") || "—"}</TableCell>
                  <TableCell className="flex gap-1">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSelected(r)} data-testid={`contact-view-${r.id}`}>View</Button>
                    {isAdmin && r.archived && (
                      <Button size="sm" variant="ghost" className="rounded-full text-primary" onClick={() => unarchive(r)} data-testid={`contact-unarchive-${r.id}`}><RotateCcw size={14} /></Button>
                    )}
                    {isAdmin && !r.archived && (
                      <Button size="sm" variant="ghost" className="rounded-full text-slate-400 hover:text-amber-600" onClick={() => setConfirmArchive(r)} data-testid={`contact-archive-${r.id}`}><Archive size={14} /></Button>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => setConfirmDelete(r)} data-testid={`contact-delete-${r.id}`}><Trash2 size={14} /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      {/* View dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{selected.name}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <div><b>Email:</b> {selected.email}</div>
                <div><b>Phone:</b> {selected.phone || "—"}</div>
                <div><b>Emergency:</b> {selected.emergency_contact || "—"}</div>
                <div><b>Address:</b> {selected.address || "—"}</div>
                <div><b>Children:</b> {selected.gymnasts.map((g) => `${g.first_name} ${g.last_name}`).join(", ") || "—"}</div>
                {isAdmin && <div><b>Bookings:</b> {selected.bookings_count}</div>}
                {isAdmin && (
                  <div>
                    <b>Payment history:</b>
                    {selected.payments.length === 0 ? " None" : (
                      <ul className="mt-1 list-disc pl-5">
                        {selected.payments.map((p) => <li key={p.id}>{p.type} — £{p.amount.toFixed(2)} ({fmtDate(p.date)})</li>)}
                      </ul>
                    )}
                  </div>
                )}
                <NoteEditor parentId={selected.id} initial={selected.notes} />
                {isAdmin && (
                  <div className="flex gap-2 pt-2 border-t border-border">
                    <Button size="sm" variant="outline" className="rounded-full text-amber-600 border-amber-300 hover:bg-amber-50"
                      onClick={() => { setSelected(null); setConfirmArchive(selected); }} data-testid="contact-dialog-archive">
                      <Archive size={14} className="mr-1" /> Archive
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/5"
                      onClick={() => { setSelected(null); setConfirmDelete(selected); }} data-testid="contact-dialog-delete">
                      <Trash2 size={14} className="mr-1" /> Permanently Delete
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm archive */}
      <PinConfirmDialog
        open={!!confirmArchive}
        onOpenChange={(o) => !o && setConfirmArchive(null)}
        title="Archive Contact?"
        description={confirmArchive ? `Archive ${confirmArchive.name}? They will be hidden from the contacts list. You can restore them at any time.` : ""}
        onConfirm={() => archive(confirmArchive)}
        confirmLabel="Archive"
        testid="confirm-archive-contact"
      />

      {/* Confirm permanent delete */}
      <PinConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Permanently Delete?"
        description={confirmDelete ? `This will permanently delete ${confirmDelete.name}, all their gymnasts, bookings, payment records, and cancel any active Stripe subscriptions. This cannot be undone.` : ""}
        onConfirm={() => permDelete(confirmDelete)}
        confirmLabel="Delete Everything"
        testid="confirm-delete-contact"
      />
    </div>
  );
}

function NoteEditor({ parentId, initial }) {
  const [notes, setNotes] = useState(initial || "");
  const save = async () => {
    try { await api.put(`/admin/contacts/${parentId}/notes?notes=${encodeURIComponent(notes)}`); toast.success("Notes saved"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div>
      <b>Internal notes:</b>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-xl mt-1" data-testid="contact-notes" />
      <Button size="sm" onClick={save} className="rounded-full mt-2 bg-primary hover:bg-primary/90" data-testid="contact-notes-save">Save Notes</Button>
    </div>
  );
}

/* ---------------- GYMNASTS ---------------- */
function Gymnasts() {
  const [rows, setRows] = useState(null);
  const [history, setHistory] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [moveGym, setMoveGym] = useState(null);
  const [moveClassId, setMoveClassId] = useState("");
  const [moveConfirm, setMoveConfirm] = useState(false);
  const [classList, setClassList] = useState([]);

  const load = () => api.get("/admin/gymnasts").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleMember = async (g) => {
    await api.put(`/admin/gymnasts/${g.id}?existing_member=${!g.existing_member}`);
    load();
  };
  const viewHistory = async (g) => {
    const { data } = await api.get(`/admin/attendance/history/${g.id}`);
    setHistory({ gymnast: g, records: data });
  };
  const deleteGym = async (g) => {
    try { await api.delete(`/admin/gymnasts/${g.id}`); toast.success("Gymnast deleted"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const openMove = async (g) => {
    const { data } = await api.get("/admin/classes");
    setClassList(data.filter((c) => c.id !== g.class_id));
    setMoveClassId("");
    setMoveGym(g);
  };
  const doMove = async () => {
    if (!moveClassId) { toast.error("Please select a class"); return; }
    try {
      const { data } = await api.put(`/admin/gymnasts/${moveGym.id}/move-class`, { new_class_id: moveClassId });
      toast.success(data.message); setMoveGym(null); setMoveConfirm(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const selectedClass = classList.find((c) => c.id === moveClassId);

  return (
    <div data-testid="admin-gymnasts">
      <Panel title="Gymnasts">
        {!rows ? <Loading /> : rows.length === 0 ? <p className="text-slate-500">No gymnasts yet.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Class</TableHead><TableHead>Parent</TableHead>
              <TableHead>Existing Member</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((g) => (
                <TableRow key={g.id}>
                  <TableCell className="font-medium">{g.first_name} {g.last_name}</TableCell>
                  <TableCell>{g.day_name} – {g.school}</TableCell>
                  <TableCell>{g.parent_name}</TableCell>
                  <TableCell><Switch checked={!!g.existing_member} onCheckedChange={() => toggleMember(g)} data-testid={`gym-member-${g.id}`} /></TableCell>
                  <TableCell className="flex gap-1">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => viewHistory(g)}>Attendance</Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openMove(g)} data-testid={`gym-move-${g.id}`} title="Move to another class"><ArrowRightLeft size={14} /></Button>
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => setConfirmDel(g)} data-testid={`gym-delete-${g.id}`}><Trash2 size={14} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      {/* Attendance history */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent>
          {history && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl">
                Attendance — {history.gymnast.first_name} {history.gymnast.last_name}
              </DialogTitle></DialogHeader>
              {history.records.length === 0 ? <p className="text-slate-500">No attendance recorded yet.</p> : (
                <div className="space-y-1 text-sm">
                  {history.records.map((r) => (
                    <div key={r.id} className="flex justify-between border-b border-border py-1">
                      <span>{fmtDate(r.session_date)}</span>
                      <Badge className={`rounded-full capitalize ${r.status === "attended" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>{r.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Move gymnast — class selection */}
      <Dialog open={!!moveGym && !moveConfirm} onOpenChange={(o) => { if (!o) { setMoveGym(null); setMoveClassId(""); } }}>
        <DialogContent>
          {moveGym && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Move Gymnast</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600">
                Moving <strong>{moveGym.first_name} {moveGym.last_name}</strong> — currently at <strong>{moveGym.school}</strong>
              </p>
              <div><Label className="mb-1 block text-sm">Move to Class</Label>
                <Select value={moveClassId} onValueChange={setMoveClassId}>
                  <SelectTrigger className="rounded-xl" data-testid="move-class-select"><SelectValue placeholder="Select a class…" /></SelectTrigger>
                  <SelectContent>
                    {classList.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {DAYS[c.day_of_week]} — {c.school} ({c.start_time}–{c.end_time}) £{c.monthly_cost?.toFixed(2)}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={() => { setMoveGym(null); setMoveClassId(""); }}>Cancel</Button>
                <Button className="rounded-full bg-primary hover:bg-primary/90 font-display" disabled={!moveClassId} onClick={() => setMoveConfirm(true)} data-testid="move-confirm-btn">Move to This Class</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Move gymnast — PIN confirmation */}
      <PinConfirmDialog
        open={moveConfirm}
        onOpenChange={(o) => { if (!o) setMoveConfirm(false); }}
        title="Confirm Class Move"
        description={moveGym && selectedClass ? `Move ${moveGym.first_name} ${moveGym.last_name} to ${DAYS[selectedClass.day_of_week]} – ${selectedClass.school}? Their Direct Debit will be updated to £${selectedClass.monthly_cost?.toFixed(2)}/month if the amount differs.` : ""}
        onConfirm={doMove}
        confirmLabel="Move"
        testid="confirm-move-gym"
      />

      {/* Delete confirmation */}
      <PinConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Delete Gymnast?"
        description={confirmDel ? `This will permanently delete ${confirmDel.first_name} ${confirmDel.last_name}, their booking, and cancel any active subscription. This cannot be undone.` : ""}
        onConfirm={() => deleteGym(confirmDel)}
        confirmLabel="Delete"
        testid="confirm-delete-gym"
      />
    </div>
  );
}

/* ---------------- CLASSES ---------------- */
const emptyClass = {
  name: "After-School Gymnastics", school: "", day_of_week: "0", start_time: "15:30", end_time: "16:30",
  monthly_cost: "30", general_capacity: "20", reserved_capacity: "0", term_start: "2026-09-01", term_end: "2027-07-24",
  status: "active", waiting_list_enabled: true, description: "",
};

function Classes() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyClass);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailClassId, setEmailClassId] = useState("");
  const [emailList, setEmailList] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  const load = () => api.get("/admin/classes").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyClass); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    const reservedCap = c.reserved_capacity || 0;
    setForm({ ...c, day_of_week: String(c.day_of_week), monthly_cost: String(c.monthly_cost), general_capacity: String(c.capacity - reservedCap), reserved_capacity: String(reservedCap) });
    setOpen(true);
  };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    const generalCap = parseInt(form.general_capacity) || 0;
    const reservedCap = parseInt(form.reserved_capacity) || 0;
    const payload = {
      ...form, day_of_week: parseInt(form.day_of_week),
      monthly_cost: parseFloat(form.monthly_cost),
      capacity: generalCap + reservedCap,
      reserved_capacity: reservedCap,
    };
    delete payload.general_capacity;
    try {
      if (editing) await api.put(`/admin/classes/${editing.id}`, payload);
      else await api.post("/admin/classes", payload);
      setOpen(false); load(); toast.success("Class saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const archive = async (id) => { await api.delete(`/admin/classes/${id}`); setConfirmArchive(null); load(); toast.success("Class deleted"); };

  const fetchEmails = async (classId) => {
    setEmailClassId(classId);
    setEmailLoading(true);
    setEmailList("");
    try {
      const { data } = await api.get(`/admin/classes/${classId}/emails`);
      setEmailList(data.emails);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setEmailLoading(false); }
  };

  return (
    <div data-testid="admin-classes">
      <Panel title="Classes" action={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setEmailOpen(true); setEmailClassId(""); setEmailList(""); }} className="rounded-full font-display" data-testid="class-email-btn">
            <Mail size={16} className="mr-1" /> Email
          </Button>
          <Button onClick={openNew} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="class-add">
            <Plus size={16} className="mr-1" /> New Class
          </Button>
        </div>
      }>
        {!rows ? <Loading /> : rows.length === 0 ? <p className="text-slate-500">No classes yet.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Day</TableHead><TableHead>School</TableHead><TableHead>Time</TableHead>
              <TableHead>£/mo</TableHead><TableHead>Booked</TableHead><TableHead>Reserved</TableHead><TableHead>Waiting</TableHead>
              <TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`class-row-${c.id}`}>
                  <TableCell className="font-medium">{c.day_name}</TableCell>
                  <TableCell>{c.school}</TableCell>
                  <TableCell>{c.start_time}-{c.end_time}</TableCell>
                  <TableCell>£{c.monthly_cost.toFixed(2)}</TableCell>
                  <TableCell>{c.booked}/{c.capacity}</TableCell>
                  <TableCell className="text-xs text-slate-500">{c.reserved_capacity > 0 ? `${c.reserved_capacity} reserved` : "—"}</TableCell>
                  <TableCell>{c.waiting}</TableCell>
                  <TableCell><Badge className="rounded-full capitalize bg-secondary/10 text-secondary">{c.status}</Badge></TableCell>
                  <TableCell className="flex gap-1">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(c)} data-testid={`class-edit-${c.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => setConfirmArchive(c)} data-testid={`class-delete-${c.id}`}><Trash2 size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Class" : "New Class"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Class Name"><Input value={form.name} onChange={set("name")} className="rounded-xl" data-testid="class-name" /></F>
            <F label="School"><Input value={form.school} onChange={set("school")} className="rounded-xl" data-testid="class-school" /></F>
            <div className="grid grid-cols-3 gap-3">
              <F label="Day">
                <Select value={form.day_of_week} onValueChange={set("day_of_week")}>
                  <SelectTrigger className="rounded-xl" data-testid="class-day"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Start"><Input type="time" value={form.start_time} onChange={set("start_time")} className="rounded-xl" data-testid="class-start" /></F>
              <F label="End"><Input type="time" value={form.end_time} onChange={set("end_time")} className="rounded-xl" data-testid="class-end" /></F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Monthly Cost (£)"><Input type="number" step="0.01" value={form.monthly_cost} onChange={set("monthly_cost")} className="rounded-xl" data-testid="class-cost" /></F>
              <div></div>
            </div>
            <div className="rounded-2xl bg-primary/5 border border-primary/15 p-3 space-y-3">
              <p className="text-xs font-semibold text-primary">Capacity</p>
              <div className="grid grid-cols-2 gap-3">
                <F label="General (new members)"><Input type="number" min="0" value={form.general_capacity} onChange={set("general_capacity")} className="rounded-xl" data-testid="class-general-capacity" /></F>
                <F label="Reserved (existing members)"><Input type="number" min="0" value={form.reserved_capacity} onChange={set("reserved_capacity")} className="rounded-xl" data-testid="class-reserved-capacity" /></F>
              </div>
              <p className="text-xs text-slate-500">Total capacity: <strong>{(parseInt(form.general_capacity) || 0) + (parseInt(form.reserved_capacity) || 0)}</strong> &nbsp;·&nbsp; Existing members use reserved spaces and pay no starter fee.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Term Start"><Input type="date" value={form.term_start} onChange={set("term_start")} className="rounded-xl" data-testid="class-term-start" /></F>
              <F label="Term End"><Input type="date" value={form.term_end} onChange={set("term_end")} className="rounded-xl" data-testid="class-term-end" /></F>
            </div>
            <F label="Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="rounded-xl" data-testid="class-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <label className="flex items-center gap-3">
              <Switch checked={form.waiting_list_enabled} onCheckedChange={set("waiting_list_enabled")} data-testid="class-waitlist" />
              <span className="text-sm">Waiting list available</span>
            </label>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="class-save">Save Class</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PinConfirmDialog
        open={!!confirmArchive}
        onOpenChange={(o) => !o && setConfirmArchive(null)}
        title="Delete Class?"
        description={confirmArchive ? `Delete the ${confirmArchive.day_name} class at ${confirmArchive.school}? It will be hidden from bookings. Existing bookings remain intact and can be recovered if needed.` : ""}
        onConfirm={() => archive(confirmArchive.id)}
        confirmLabel="Delete Class"
        testid="confirm-delete-class"
      />

      {/* Email list dialog */}
      <Dialog open={emailOpen} onOpenChange={(o) => { setEmailOpen(o); if (!o) { setEmailClassId(""); setEmailList(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Class Email List</DialogTitle>
          </DialogHeader>
          <div><Label className="mb-1 block text-sm">Select Class</Label>
            <Select value={emailClassId} onValueChange={fetchEmails}>
              <SelectTrigger className="rounded-xl" data-testid="email-class-select"><SelectValue placeholder="Choose a class…" /></SelectTrigger>
              <SelectContent>
                {rows && rows.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {DAYS[c.day_of_week]} — {c.school}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {emailLoading && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
          {emailClassId && !emailLoading && (
            <div className="space-y-2">
              <Label className="text-sm">Parent email addresses</Label>
              <Textarea
                readOnly
                value={emailList || "(No active enrolments in this class)"}
                className="rounded-xl min-h-[80px] font-mono text-xs"
                data-testid="email-list-output"
              />
              {emailList && (
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => { navigator.clipboard.writeText(emailList); toast.success("Copied to clipboard"); }} data-testid="copy-emails-btn">
                  <Check size={14} className="mr-1" /> Copy to Clipboard
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => (<div><Label className="mb-1 block text-sm">{label}</Label>{children}</div>);

/* ---------------- BOOKINGS ---------------- */
function Bookings() {
  const [rows, setRows] = useState(null);
  const [leaver, setLeaver] = useState(null);
  const [date, setDate] = useState("");
  const [viewBooking, setViewBooking] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/admin/bookings").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openLeaver = (b) => { setLeaver(b); setDate(b.last_session_date || ""); };
  const saveLeaver = async () => {
    if (!date) { toast.error("Please choose a last session date"); return; }
    try {
      const { data } = await api.put(`/admin/bookings/${leaver.id}/leaver?last_session_date=${date}`);
      toast.success(data.message); setLeaver(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const clearLeaver = async (b) => {
    try { await api.delete(`/admin/bookings/${b.id}/leaver`); toast.success("Leaver date cleared"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const openView = async (b) => {
    setViewLoading(true); setViewBooking({});
    try { const { data } = await api.get(`/admin/bookings/${b.id}`); setViewBooking(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); setViewBooking(null); }
    finally { setViewLoading(false); }
  };
  const deleteBooking = async (b) => {
    try { await api.delete(`/admin/bookings/${b.id}`); toast.success("Booking and subscription deleted"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const statusColor = (s) => s === "active" ? "bg-success/10 text-success" : s === "left" ? "bg-slate-200 text-slate-600" : "bg-accent/10 text-accent-foreground";

  return (
    <div data-testid="admin-bookings">
      <Panel title="Bookings">
        {!rows ? <Loading /> : rows.length === 0 ? <p className="text-slate-500">No bookings yet.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Gymnast</TableHead><TableHead>Parent</TableHead><TableHead>Class</TableHead>
              <TableHead>£/mo</TableHead><TableHead>Bills</TableHead><TableHead>Last Session</TableHead>
              <TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                  <TableCell className="font-medium">{b.gymnast_name}</TableCell>
                  <TableCell>{b.parent_name}</TableCell>
                  <TableCell>{b.day_name} – {b.class_name}</TableCell>
                  <TableCell>£{b.monthly_cost.toFixed(2)}</TableCell>
                  <TableCell>{b.billing_day ? `${ordinal(b.billing_day)} monthly` : "—"}</TableCell>
                  <TableCell>{b.last_session_date ? fmtDate(b.last_session_date) : "—"}</TableCell>
                  <TableCell><Badge className={`rounded-full capitalize ${statusColor(b.status)}`}>{b.status}</Badge></TableCell>
                  <TableCell className="flex gap-1">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openView(b)} data-testid={`view-btn-${b.id}`}>
                      <Eye size={14} className="mr-1" />View
                    </Button>
                    {b.status === "active" && (
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openLeaver(b)} data-testid={`leaver-btn-${b.id}`}>Leaver</Button>
                    )}
                    {b.last_session_date && b.status === "active" && (
                      <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => clearLeaver(b)}>Undo</Button>
                    )}
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => setConfirmDel(b)} data-testid={`booking-delete-${b.id}`}><Trash2 size={14} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      {/* Leaver Dialog */}
      <Dialog open={!!leaver} onOpenChange={(o) => !o && setLeaver(null)}>
        <DialogContent>
          {leaver && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Set Leaver Date</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-600">
                Enter <b>{leaver.gymnast_name}</b>'s last session date. They will not be charged after this date, and once it passes
                they are automatically removed from the class, register and your Contacts. Past payment records are kept.
              </p>
              <F label="Last Session Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl" data-testid="leaver-date" /></F>
              <DialogFooter>
                <Button onClick={saveLeaver} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="leaver-save">Save Leaver Date</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete booking/subscription */}
      <PinConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Delete Booking?"
        description={confirmDel ? `This will permanently delete the booking for ${confirmDel.gymnast_name} and cancel their Stripe subscription. Payment records are kept. This cannot be undone.` : ""}
        onConfirm={() => deleteBooking(confirmDel)}
        confirmLabel="Delete Booking"
        testid="confirm-delete-booking"
      />

      {/* View Booking Dialog */}
      <Dialog open={viewBooking !== null} onOpenChange={(o) => !o && setViewBooking(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Booking Details</DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-primary" size={36} /></div>
          ) : viewBooking && Object.keys(viewBooking).length > 0 && (
            <div className="space-y-6 py-2">

              {/* Gymnast */}
              <DetailSection title="Gymnast">
                <DetailRow label="Full Name" testid="detail-gymnast-name">{viewBooking.gymnast?.first_name} {viewBooking.gymnast?.last_name}</DetailRow>
                <DetailRow label="Date of Birth" testid="detail-gymnast-dob">{viewBooking.gymnast?.dob || "—"}</DetailRow>
                <DetailRow label="T-shirt Size" testid="detail-gymnast-tshirt">{viewBooking.gymnast?.tshirt_size || "—"}</DetailRow>
                <DetailRow label="Photography Consent" testid="detail-photo-consent">{viewBooking.gymnast?.photography_consent ? "Yes" : "No"}</DetailRow>
                {viewBooking.gymnast?.medical_info && (
                  <DetailRow label="Medical Information" testid="detail-medical-info">{viewBooking.gymnast.medical_info}</DetailRow>
                )}
                {!viewBooking.gymnast?.medical_info && (
                  <DetailRow label="Medical Information" testid="detail-medical-info-none"><span className="text-slate-400 italic">None provided</span></DetailRow>
                )}
              </DetailSection>

              {/* Parent / Adult */}
              <DetailSection title="Parent / Adult">
                <DetailRow label="Full Name" testid="detail-adult-name">{viewBooking.adult_name || "—"}</DetailRow>
                <DetailRow label="Email" testid="detail-adult-email">{viewBooking.adult_email || "—"}</DetailRow>
                <DetailRow label="Phone / Emergency Contact" testid="detail-adult-phone">{viewBooking.phone || viewBooking.emergency_contact || "—"}</DetailRow>
                <DetailRow label="Address" testid="detail-adult-address">{viewBooking.address || "—"}</DetailRow>
                <DetailRow label="Marketing Consent" testid="detail-marketing">{viewBooking.marketing_consent ? "Yes" : "No"}</DetailRow>
              </DetailSection>

              {/* Class & Booking */}
              <DetailSection title="Class &amp; Booking">
                <DetailRow label="Class" testid="detail-class">{viewBooking.class_info?.day_name} – {viewBooking.class_info?.school}</DetailRow>
                <DetailRow label="Time" testid="detail-time">{viewBooking.class_info?.start_time} – {viewBooking.class_info?.end_time}</DetailRow>
                <DetailRow label="Monthly Cost" testid="detail-monthly">£{viewBooking.monthly_cost?.toFixed(2)}</DetailRow>
                <DetailRow label="Starter Fee Paid" testid="detail-starter">£{(viewBooking.starter_fee_paid ?? 0).toFixed(2)}</DetailRow>
                <DetailRow label="First Billing Date" testid="detail-first-billing">{fmtDate(viewBooking.first_billing_date)}</DetailRow>
                <DetailRow label="Billing Day" testid="detail-billing-day">{viewBooking.billing_day ? `${ordinal(viewBooking.billing_day)} of each month` : "—"}</DetailRow>
                <DetailRow label="Status" testid="detail-status"><span className="capitalize">{viewBooking.status}</span></DetailRow>
                <DetailRow label="Reserved Space" testid="detail-reserved">{viewBooking.is_reserved ? "Yes" : "No"}</DetailRow>
                <DetailRow label="Booked On" testid="detail-created">{fmtDate(viewBooking.created_at)}</DetailRow>
              </DetailSection>

              {/* Signature */}
              {viewBooking.signature && viewBooking.signature.startsWith("data:image") && (
                <DetailSection title="Signature">
                  <div className="border border-border rounded-2xl overflow-hidden bg-white p-3" data-testid="detail-signature-img">
                    <img src={viewBooking.signature} alt="Parent / Guardian Signature" className="max-h-36 w-auto" />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Digital signature provided at time of booking</p>
                </DetailSection>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- PAYMENTS ---------------- */
/* ---------------- PAYMENT FAILURES ---------------- */
function PaymentFailures({ onRetried }) {
  const [rows, setRows] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const load = () => api.get("/admin/payment-failures").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const retry = async (f) => {
    if (!f.invoice_id) { toast.error("No invoice ID — cannot retry automatically"); return; }
    setRetrying(f.payment_id);
    try {
      const { data } = await api.post(`/admin/invoice/${f.invoice_id}/retry-payment`);
      toast.success(data.message);
      load();
      if (onRetried) onRetried();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setRetrying(null); }
  };

  if (!rows) return null;
  if (rows.length === 0) return (
    <div className="rounded-2xl bg-success/10 border border-success/30 p-4 flex items-center gap-3 mb-6" data-testid="no-failures-notice">
      <Check size={18} className="text-success shrink-0" />
      <span className="text-sm text-slate-700 font-medium">No unresolved payment failures.</span>
    </div>
  );

  return (
    <Panel title="Payment Failures" testid="payment-failures-panel">
      <div className="flex items-center gap-2 mb-4 text-sm text-destructive font-medium">
        <AlertTriangle size={16} />
        {rows.length} unresolved failure{rows.length !== 1 ? "s" : ""} — Direct Debit collection failed for the families below.
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Child</TableHead><TableHead>Parent</TableHead><TableHead>Class</TableHead>
          <TableHead>Amount</TableHead><TableHead>Failed</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((f) => (
            <TableRow key={f.payment_id} className="bg-destructive/5">
              <TableCell className="font-medium">{f.gymnast_name}</TableCell>
              <TableCell>
                <div>{f.parent_name}</div>
                <div className="text-xs text-slate-500">{f.parent_email}</div>
              </TableCell>
              <TableCell>{f.day_name} – {f.school}</TableCell>
              <TableCell>£{f.amount?.toFixed(2)}</TableCell>
              <TableCell>{fmtDate(f.date)}</TableCell>
              <TableCell>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-destructive text-destructive hover:bg-destructive hover:text-white"
                  disabled={retrying === f.payment_id || !f.invoice_id}
                  onClick={() => retry(f)}
                  data-testid={`retry-payment-${f.payment_id}`}
                  title={!f.invoice_id ? "No invoice ID — retry manually in Stripe" : "Retry Direct Debit collection"}
                >
                  {retrying === f.payment_id ? <Loader2 size={14} className="animate-spin" /> : "Retry"}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

/* ---------------- PAYMENTS ---------------- */
function Payments() {
  const [rows, setRows] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => api.get("/admin/payments").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const deletePayment = async (p) => {
    try { await api.delete(`/admin/payments/${p.id}`); toast.success("Payment record deleted"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-payments">
      <PaymentFailures key={refreshKey} onRetried={() => { setRefreshKey((k) => k + 1); load(); }} />
      <Panel title="All Payments">
        {!rows ? <Loading /> : rows.length === 0 ? <p className="text-slate-500">No payments recorded.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Parent</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead>
              <TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.parent_name}</TableCell>
                  <TableCell>{p.type}</TableCell>
                  <TableCell>£{p.amount.toFixed(2)}</TableCell>
                  <TableCell>{fmtDate(p.date)}</TableCell>
                  <TableCell>
                    <Badge className={`rounded-full capitalize ${p.status === "failed" ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}>
                      {p.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => setConfirmDel(p)} data-testid={`payment-delete-${p.id}`}><Trash2 size={14} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <PinConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Delete Payment Record?"
        description={confirmDel ? `Remove the £${confirmDel.amount?.toFixed(2)} ${confirmDel.type} payment record for ${confirmDel.parent_name}? This only removes the record — it does not issue a Stripe refund.` : ""}
        onConfirm={() => deletePayment(confirmDel)}
        confirmLabel="Delete Record"
        testid="confirm-delete-payment"
      />
    </div>
  );
}

/* ---------------- WAITING LIST ---------------- */
function Waiting() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState({ class_id: "", child_first_name: "", child_last_name: "", adult_name: "", adult_email: "", phone: "", notes: "" });

  const load = () => {
    setError(null);
    api.get("/admin/waiting-list")
      .then((r) => setRows(r.data))
      .catch((e) => { setRows([]); setError(apiErr(e.response?.data?.detail) || "Failed to load waiting list."); });
  };
  useEffect(() => { load(); api.get("/admin/classes").then((r) => setClasses(r.data.filter((c) => c.status === "active"))).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const addEntry = async () => {
    if (!form.class_id || !form.child_first_name || !form.child_last_name || !form.adult_name || !form.adult_email || !form.phone) {
      toast.error("Please fill in all required fields."); return;
    }
    try {
      await api.post("/admin/waiting-list", form);
      toast.success("Added to waiting list"); setAdding(false);
      setForm({ class_id: "", child_first_name: "", child_last_name: "", adult_name: "", adult_email: "", phone: "", notes: "" });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/admin/waiting-list/${id}`); load(); };
  const active = rows?.filter((r) => r.status === "waiting");

  return (
    <div data-testid="admin-waiting">
      <Panel title="Waiting Lists" action={
        <Button onClick={() => setAdding(true)} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="wl-add-btn">
          <Plus size={16} className="mr-1" /> Add Manually
        </Button>
      }>
        {error && <p className="text-destructive text-sm mb-3">{error}</p>}
        {!rows ? <Loading /> : !active?.length ? <p className="text-slate-500">No one on the waiting list.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Child</TableHead><TableHead>Parent</TableHead><TableHead>Class</TableHead>
              <TableHead>Contact</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {active.map((w) => (
                <TableRow key={w.id}>
                  <TableCell className="font-medium">{w.child_first_name} {w.child_last_name}</TableCell>
                  <TableCell>{w.adult_name}</TableCell>
                  <TableCell>{w.day_name ? `${w.day_name} – ` : ""}{w.class_name}<br /><span className="text-xs text-slate-500">{w.school}</span></TableCell>
                  <TableCell>{w.phone}<br /><span className="text-xs text-slate-500">{w.adult_email}</span></TableCell>
                  <TableCell className="flex gap-1">
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => remove(w.id)}><Trash2 size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Dialog open={adding} onOpenChange={(o) => !o && setAdding(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add to Waiting List</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Class">
              <Select value={form.class_id} onValueChange={set("class_id")}>
                <SelectTrigger className="rounded-xl" data-testid="wl-class"><SelectValue placeholder="Select class..." /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{DAYS[c.day_of_week]} – {c.school} ({c.start_time})</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Child First Name"><Input value={form.child_first_name} onChange={set("child_first_name")} className="rounded-xl" data-testid="wl-child-first" /></F>
              <F label="Child Last Name"><Input value={form.child_last_name} onChange={set("child_last_name")} className="rounded-xl" data-testid="wl-child-last" /></F>
            </div>
            <F label="Parent / Guardian Name"><Input value={form.adult_name} onChange={set("adult_name")} className="rounded-xl" data-testid="wl-adult-name" /></F>
            <F label="Email"><Input type="email" value={form.adult_email} onChange={set("adult_email")} className="rounded-xl" data-testid="wl-adult-email" /></F>
            <F label="Phone"><Input value={form.phone} onChange={set("phone")} className="rounded-xl" data-testid="wl-phone" /></F>
            <F label="Notes (optional)"><Input value={form.notes} onChange={set("notes")} className="rounded-xl" data-testid="wl-notes" /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={addEntry} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="wl-add-save">Add to List</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- ATTENDANCE ---------------- */
function Attendance() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [date, setDate] = useState("");
  const [register, setRegister] = useState(null);
  const [gymProfile, setGymProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => { api.get("/admin/classes").then((r) => setClasses(r.data.filter((c) => c.status === "active"))).catch(() => {}); }, []);
  useEffect(() => {
    if (!classId) return;
    setDate(""); setRegister(null);
    api.get(`/admin/attendance/sessions?class_id=${classId}`).then((r) => setSessions(r.data)).catch(() => {});
  }, [classId]);

  const loadRegister = useCallback(() => {
    if (!classId || !date) return;
    api.get(`/admin/attendance/register?class_id=${classId}&session_date=${date}`).then((r) => setRegister(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, [classId, date]);
  useEffect(() => { loadRegister(); }, [loadRegister]);

  const mark = async (gymnastId, status) => {
    try {
      await api.post("/admin/attendance/mark", { class_id: classId, session_date: date, gymnast_id: gymnastId, status, notes: "" });
      loadRegister();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const openProfile = async (g) => {
    setGymProfile({});
    setProfileLoading(true);
    try {
      const { data } = await api.get(`/admin/gymnasts/${g.gymnast_id}/profile`);
      setGymProfile(data);
    } catch (e) {
      toast.error("Could not load gymnast profile");
      setGymProfile(null);
    } finally { setProfileLoading(false); }
  };

  const attended = register?.register?.filter((g) => g.status === "attended").length ?? 0;
  const absent = register?.register?.filter((g) => g.status === "absent").length ?? 0;
  const unmarked = register?.register?.filter((g) => g.status === "unmarked").length ?? 0;

  return (
    <div data-testid="admin-attendance">
      <Panel title="Attendance Register">
        <div className="grid sm:grid-cols-2 gap-4 mb-4 max-w-xl">
          <div>
            <Label className="mb-1 block text-sm">Class</Label>
            <Select value={classId} onValueChange={setClassId}>
              <SelectTrigger className="rounded-xl" data-testid="att-class"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.day_name} – {c.school}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-sm">Session Date</Label>
            <Select value={date} onValueChange={setDate} disabled={!classId}>
              <SelectTrigger className="rounded-xl" data-testid="att-date"><SelectValue placeholder="Select session" /></SelectTrigger>
              <SelectContent>{sessions.map((s) => <SelectItem key={s} value={s}>{fmtDate(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {register && (
          <div data-testid="att-register">
            {/* Summary row */}
            <div className="flex gap-3 mb-4">
              <div className="flex items-center gap-1.5 rounded-full bg-success/10 text-success px-3 py-1 text-sm font-medium">
                <Check size={14} strokeWidth={2.5} />{attended} present
              </div>
              <div className="flex items-center gap-1.5 rounded-full bg-destructive/10 text-destructive px-3 py-1 text-sm font-medium">
                <X size={14} strokeWidth={2.5} />{absent} absent
              </div>
              {unmarked > 0 && (
                <div className="flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-500 px-3 py-1 text-sm font-medium">
                  {unmarked} unmarked
                </div>
              )}
            </div>

            {register.register.length === 0 ? <p className="text-slate-500">No gymnasts booked into this class.</p> : (
              <div className="space-y-2">
                {register.register.map((g) => (
                  <div key={g.gymnast_id} className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${
                    g.status === "attended" ? "border-success/30 bg-success/5" :
                    g.status === "absent" ? "border-destructive/20 bg-destructive/5" :
                    "border-border hover:border-primary/30"
                  }`}>
                    {/* Status dot */}
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                      g.status === "attended" ? "bg-success" :
                      g.status === "absent" ? "bg-destructive" : "bg-slate-300"
                    }`} />

                    {/* Clickable name */}
                    <button
                      className="font-medium text-sm text-left flex-1 hover:text-primary hover:underline underline-offset-2 transition-colors"
                      onClick={() => openProfile(g)}
                      data-testid={`att-name-${g.gymnast_id}`}
                    >
                      {g.name}
                    </button>

                    {/* Tick / Cross */}
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => mark(g.gymnast_id, "attended")}
                        data-testid={`att-present-${g.gymnast_id}`}
                        title="Mark Present"
                        className={`w-9 h-9 rounded-full flex items-center justify-center transition-all font-bold ${
                          g.status === "attended"
                            ? "bg-success text-white shadow-md scale-105"
                            : "bg-success/10 text-success hover:bg-success hover:text-white"
                        }`}
                      >
                        <Check size={16} strokeWidth={3} />
                      </button>
                      <button
                        onClick={() => mark(g.gymnast_id, "absent")}
                        data-testid={`att-absent-${g.gymnast_id}`}
                        title="Mark Absent"
                        className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                          g.status === "absent"
                            ? "bg-destructive text-white shadow-md scale-105"
                            : "bg-destructive/10 text-destructive hover:bg-destructive hover:text-white"
                        }`}
                      >
                        <X size={16} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Panel>

      {/* Gymnast Profile Dialog */}
      <Dialog open={gymProfile !== null} onOpenChange={(o) => !o && setGymProfile(null)}>
        <DialogContent className="max-w-md" data-testid="att-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {profileLoading ? "Loading…" : gymProfile ? `${gymProfile.first_name} ${gymProfile.last_name}` : ""}
            </DialogTitle>
          </DialogHeader>
          {profileLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-primary" size={32} /></div>
          ) : gymProfile && Object.keys(gymProfile).length > 0 && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-slate-50 p-3">
                  <div className="text-xs text-slate-500 mb-0.5">Date of Birth</div>
                  <div className="font-semibold" data-testid="profile-dob">{gymProfile.dob || "—"}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <div className="text-xs text-slate-500 mb-0.5">T-shirt Size</div>
                  <div className="font-semibold" data-testid="profile-tshirt">{gymProfile.tshirt_size || "—"}</div>
                </div>
              </div>
              {gymProfile.class_name && (
                <div className="rounded-xl bg-slate-50 p-3">
                  <div className="text-xs text-slate-500 mb-0.5">Class</div>
                  <div className="font-semibold">{gymProfile.class_name}</div>
                  {gymProfile.class_time && <div className="text-xs text-slate-500 mt-0.5">{gymProfile.class_time}</div>}
                </div>
              )}
              <div className="rounded-xl bg-slate-50 p-3 space-y-2">
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Parent / Guardian</div>
                <ProfileRow label="Name" testid="profile-parent-name">{gymProfile.parent_name || "—"}</ProfileRow>
                <ProfileRow label="Phone" testid="profile-phone">{gymProfile.phone || "—"}</ProfileRow>
                <ProfileRow label="Emergency" testid="profile-emergency">{gymProfile.emergency_contact || "—"}</ProfileRow>
                <ProfileRow label="Email" testid="profile-email">{gymProfile.parent_email || "—"}</ProfileRow>
              </div>
              {gymProfile.medical_info ? (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="profile-medical">
                  <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide mb-1">Medical Information</div>
                  <div className="text-slate-700">{gymProfile.medical_info}</div>
                </div>
              ) : (
                <div className="rounded-xl bg-slate-50 p-3" data-testid="profile-medical-none">
                  <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-0.5">Medical Information</div>
                  <div className="text-slate-400 italic text-xs">None provided</div>
                </div>
              )}
              <div className="rounded-xl bg-slate-50 p-3">
                <ProfileRow label="Photography" testid="profile-photo">{gymProfile.photography_consent ? "Consent given" : "No consent"}</ProfileRow>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- CALENDAR ---------------- */
function CalendarView() {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get("/admin/calendar").then((r) => setEvents(r.data)).catch(() => {}); }, []);
  const upcoming = events ? [...events].filter((e) => new Date(e.date) >= new Date(new Date().toDateString()))
    .sort((a, b) => a.date.localeCompare(b.date)).slice(0, 60) : [];
  return (
    <div data-testid="admin-calendar">
      <Panel title="Calendar">
        <p className="text-sm text-slate-500 mb-4">Sessions and exclusion dates. Excluded dates are greyed out; no sessions run on them.</p>
        {!events ? <Loading /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcoming.map((e, i) => (
              <div key={i} className={`rounded-2xl border p-4 ${e.type === "exclusion" ? "bg-slate-100 border-slate-200 opacity-70" : "border-border hover:border-primary"}`}>
                <div className={`font-display text-lg ${e.type === "exclusion" ? "text-slate-400 line-through" : "text-primary"}`}>{fmtDate(e.date)}</div>
                <div className="text-slate-700 text-sm">{e.title}</div>
                {e.type === "session" ? <div className="text-slate-500 text-sm">{e.school} · {e.time}</div>
                  : <Badge className="mt-1 rounded-full bg-slate-200 text-slate-500">Excluded</Badge>}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

/* ---------------- EXCLUSIONS ---------------- */
function Exclusions() {
  const [rows, setRows] = useState(null);
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "Half Term", applies_to: "all", class_ids: [] });

  const load = () => api.get("/admin/exclusions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const add = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Please set start and end dates"); return; }
    try {
      await api.post("/admin/exclusions", { ...form, end_date: form.end_date || form.start_date });
      setForm({ start_date: "", end_date: "", reason: "Half Term", applies_to: "all", class_ids: [] });
      load(); toast.success("Exclusion added");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/exclusions/${id}`); load(); };

  return (
    <div data-testid="admin-exclusions">
      <Panel title="Add Exclusion">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <F label="Start Date"><Input type="date" value={form.start_date} onChange={set("start_date")} className="rounded-xl" data-testid="excl-start" /></F>
          <F label="End Date"><Input type="date" value={form.end_date} onChange={set("end_date")} className="rounded-xl" data-testid="excl-end" /></F>
          <F label="Reason">
            <Select value={form.reason} onValueChange={set("reason")}>
              <SelectTrigger className="rounded-xl" data-testid="excl-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Half Term", "Summer Break", "Bank Holiday", "School Closed", "Inset Day", "Christmas Break", "Easter Break"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Applies To">
            <Select value={form.applies_to} onValueChange={set("applies_to")}>
              <SelectTrigger className="rounded-xl" data-testid="excl-applies"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                <SelectItem value="selected">Selected Classes</SelectItem>
              </SelectContent>
            </Select>
          </F>
        </div>
        {form.applies_to === "selected" && (
          <div className="mt-3 flex flex-wrap gap-2">
            {classes.map((c) => {
              const on = form.class_ids.includes(c.id);
              return (
                <button key={c.id} onClick={() => set("class_ids")(on ? form.class_ids.filter((x) => x !== c.id) : [...form.class_ids, c.id])}
                  className={`rounded-full px-3 py-1 text-sm border ${on ? "bg-primary text-white border-primary" : "border-border text-slate-600"}`}>
                  {c.day_name} – {c.school}
                </button>
              );
            })}
          </div>
        )}
        <Button onClick={add} className="rounded-full font-display bg-primary hover:bg-primary/90 mt-4" data-testid="excl-add">
          <Plus size={16} className="mr-1" /> Add Exclusion
        </Button>
      </Panel>

      <Panel title="Exclusion Dates">
        {!rows ? <Loading /> : rows.length === 0 ? <p className="text-slate-500">No exclusions set.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Reason</TableHead>
              <TableHead>Applies To</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id} data-testid={`excl-row-${e.id}`}>
                  <TableCell>{fmtDate(e.start_date)}</TableCell>
                  <TableCell>{fmtDate(e.end_date)}</TableCell>
                  <TableCell>{e.reason}</TableCell>
                  <TableCell className="capitalize">{e.applies_to === "all" ? "All classes" : `${e.class_ids.length} class(es)`}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => del(e.id)}><Trash2 size={16} /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
    </div>
  );
}

/* ---------------- SETTINGS ---------------- */
/* ---------------- RESERVED MEMBERS ---------------- */
function ReservedMembers() {
  const [members, setMembers] = useState(null);
  const [email, setEmail] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/reserved-members").then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email.trim()) return;
    try { await api.post("/admin/reserved-members", { email: email.trim() }); setEmail(""); load(); toast.success("Email added to reserved list"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const del = async (id) => { await api.delete(`/admin/reserved-members/${id}`); load(); toast.success("Removed"); };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/admin/reserved-members/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.added} email${res.data.added !== 1 ? "s" : ""}${res.data.skipped ? ` (${res.data.skipped} already existed)` : ""}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setImporting(false); if (e.target) e.target.value = ""; }
  };

  return (
    <div data-testid="admin-reserved-members">
      <Panel title="Import Existing Contacts">
        <p className="text-sm text-slate-500 mb-4">
          These are your returning/existing members. Upload a CSV or XLSX where <strong>Column A</strong> holds email addresses,
          or add them individually. At checkout, they'll use reserved class spaces and will <strong>not</strong> be charged the starter fee.
        </p>

        <div className="flex flex-wrap gap-3 mb-4">
          <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={importFile} className="hidden" data-testid="reserved-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={importing} variant="outline" className="rounded-full font-display border-primary text-primary hover:bg-primary hover:text-white" data-testid="reserved-import-btn">
            <Upload size={16} className="mr-1.5" /> {importing ? "Importing…" : "Import CSV / XLSX"}
          </Button>
        </div>

        <div className="flex gap-2 mb-4">
          <Input placeholder="Add individual email address" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} className="rounded-xl" data-testid="reserved-email-input" />
          <Button onClick={add} className="rounded-full font-display bg-primary hover:bg-primary/90 shrink-0" data-testid="reserved-add-btn">
            <Plus size={16} className="mr-1" /> Add
          </Button>
        </div>

        {!members ? <p className="text-slate-400 text-sm">Loading…</p> : members.length === 0 ? (
          <p className="text-slate-500 text-sm">No reserved member emails yet.</p>
        ) : (
          <>
            <p className="text-xs text-slate-400 mb-2">{members.length} email{members.length !== 1 ? "s" : ""} on the reserved list</p>
            <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
              {members.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-sm" data-testid={`reserved-row-${m.id}`}>
                  <span className="font-mono text-slate-700">{m.email}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-400 capitalize">{m.source}</span>
                    <button onClick={() => del(m.id)} className="text-slate-400 hover:text-destructive" data-testid={`reserved-del-${m.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}


function SettingsSection() {
  const [starterFee, setStarterFee] = useState("");
  const [waivers, setWaivers] = useState([]);
  const [perSession, setPerSession] = useState("");
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");

  const load = () => api.get("/admin/settings").then((r) => {
    setStarterFee(String(r.data.settings.starter_fee ?? 30));
    setPerSession(String(r.data.settings.per_session_fee ?? 7.5));
    setWaivers(r.data.waivers);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveFee = async () => {
    try { await api.put(`/admin/settings?starter_fee=${parseFloat(starterFee)}&per_session_fee=${parseFloat(perSession)}`); toast.success("Fees updated"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addWaiver = async () => {
    if (!code) return;
    try { await api.post(`/admin/waivers?code=${encodeURIComponent(code)}&reason=${encodeURIComponent(reason)}`); setCode(""); setReason(""); load(); toast.success("Waiver code created"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delWaiver = async (id) => { await api.delete(`/admin/waivers/${id}`); load(); };

  return (
    <>
      <div data-testid="admin-settings">
        <Panel title="Fees">        <p className="text-sm text-slate-500 mb-3">The starter fee is a one-off charge for new gymnasts. The per-session fee is used to calculate each parent's pro-rata first payment.</p>
          <div className="flex flex-wrap items-end gap-3">
            <F label="Starter Fee (£)"><Input type="number" step="0.01" value={starterFee} onChange={(e) => setStarterFee(e.target.value)} className="rounded-xl w-40" data-testid="set-starter-fee" /></F>
            <F label="Per-Session Fee (£)"><Input type="number" step="0.01" value={perSession} onChange={(e) => setPerSession(e.target.value)} className="rounded-xl w-40" data-testid="set-per-session-fee" /></F>
            <Button onClick={saveFee} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="set-save-fee">Save</Button>
          </div>
        </Panel>

        <Panel title="Starter Fee Waiver Codes">
          <p className="text-sm text-slate-500 mb-3">Create codes parents can enter at checkout to waive the starter fee. Existing members are waived automatically.</p>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <Input placeholder="CODE" value={code} onChange={(e) => setCode(e.target.value)} className="rounded-xl" data-testid="waiver-code" />
            <Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-xl" data-testid="waiver-reason" />
            <Button onClick={addWaiver} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="waiver-add"><Plus size={16} className="mr-1" /> Add Code</Button>
          </div>
          {waivers.length === 0 ? <p className="text-slate-500">No waiver codes yet.</p> : (
            <div className="space-y-2">
              {waivers.map((w) => (
                <div key={w.id} className="flex items-center justify-between rounded-2xl border border-border p-3">
                  <div><span className="font-mono font-bold">{w.code}</span> <span className="text-sm text-slate-500">{w.reason}</span></div>
                  <button onClick={() => delWaiver(w.id)} className="text-slate-400 hover:text-destructive"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
      <ReservedMembers />
      <ForgottenPasswordPanel />
      <StaffManagement />
    </>
  );
}


/* ---------------- FORGOTTEN PASSWORD PANEL ---------------- */
function ForgottenPasswordPanel() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const [tempPasswords, setTempPasswords] = useState({}); // user_id → temp password (shown once)

  const search = (val) => {
    setQ(val);
    if (val.length < 2) { setUsers(null); return; }
    api.get(`/admin/users?q=${encodeURIComponent(val)}`)
      .then((r) => setUsers(r.data))
      .catch(() => setUsers([]));
  };

  const generateFP = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/generate-fp`);
      setTempPasswords((p) => ({ ...p, [u.id]: data.temp_password }));
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, fp_enabled: true } : x));
      toast.success(`Temporary password generated for ${u.name}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const disableFP = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/disable-fp`);
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, fp_enabled: false } : x));
      setTempPasswords((p) => { const n = { ...p }; delete n[u.id]; return n; });
      toast.success(`FP disabled for ${u.name}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="fp-panel">
      <Panel title="Forgotten Passwords">
        <p className="text-sm text-slate-500 mb-4">
          Search for a parent account, generate a temporary password, and enable FP. They log in with the
          temporary password and are immediately prompted to set a new one. FP is cleared automatically after they reset.
        </p>
        <div className="mb-4">
          <Input
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => search(e.target.value)}
            className="rounded-xl max-w-sm"
            data-testid="fp-search"
          />
        </div>
        {users === null && q.length >= 2 && <Loading />}
        {users?.length === 0 && <p className="text-slate-500 text-sm">No accounts found.</p>}
        {users?.length > 0 && (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="rounded-2xl border border-border px-4 py-3 space-y-2" data-testid={`fp-user-${u.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-medium text-sm">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.fp_enabled
                      ? <Badge className="rounded-full bg-amber-100 text-amber-700 border-amber-200">FP Active</Badge>
                      : <Badge className="rounded-full bg-slate-100 text-slate-500">Normal</Badge>
                    }
                    {!u.fp_enabled && (
                      <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90" onClick={() => generateFP(u)} data-testid={`fp-generate-${u.id}`}>
                        Generate Password
                      </Button>
                    )}
                    {u.fp_enabled && (
                      <>
                        {!tempPasswords[u.id] && (
                          <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90" onClick={() => generateFP(u)} data-testid={`fp-regen-${u.id}`}>
                            Regenerate
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-full text-destructive border-destructive/30" onClick={() => disableFP(u)} data-testid={`fp-disable-${u.id}`}>
                          Disable FP
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {tempPasswords[u.id] && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-amber-700 font-semibold mb-0.5">Temporary password (shown once — copy it now):</p>
                      <p className="font-mono font-bold text-sm tracking-wider text-slate-800 select-all" data-testid={`fp-temp-${u.id}`}>{tempPasswords[u.id]}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="rounded-full text-xs text-amber-700 shrink-0"
                      onClick={() => { navigator.clipboard.writeText(tempPasswords[u.id]); toast.success("Copied!"); }}>
                      Copy
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}


/* ---------------- STAFF MANAGEMENT ---------------- */
function StaffManagement() {
  const [staff, setStaff] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addStaff = async () => {
    if (!email || !name || !pin) { toast.error("Please fill in all fields"); return; }
    if (!/^\d{4}$/.test(pin)) { toast.error("PIN must be exactly 4 digits"); return; }
    try {
      await api.post(`/admin/staff?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}&pin=${pin}`);
      toast.success(`Staff member ${name} added`); setEmail(""); setName(""); setPin(""); setAdding(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const removeStaff = async (s) => {
    try { await api.delete(`/admin/staff/${s.id}`); toast.success("Staff member removed"); setConfirmDel(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Panel title="Staff Members" action={
      <Button onClick={() => setAdding(true)} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="staff-add-btn">
        <UserPlus size={15} className="mr-1" /> Add Staff
      </Button>
    }>
      <p className="text-sm text-slate-500 mb-4">
        Staff members can log in with their email and 4-digit PIN. They have access to Contacts and Attendance only — no payments, bookings or settings.
      </p>
      {!staff ? <Loading /> : staff.length === 0 ? <p className="text-slate-500">No staff members yet.</p> : (
        <div className="space-y-2">
          {staff.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-2xl border border-border px-4 py-3" data-testid={`staff-row-${s.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <ShieldCheck size={16} className="text-primary" />
                </div>
                <div>
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-xs text-slate-500">{s.email}</div>
                </div>
              </div>
              <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => setConfirmDel(s)} data-testid={`staff-delete-${s.id}`}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      )}

      {/* Add staff dialog */}
      <Dialog open={adding} onOpenChange={(o) => !o && setAdding(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add Staff Member</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Staff can access Contacts and Attendance. They log in with their email and 4-digit PIN.</p>
          <div className="space-y-3">
            <F label="Full Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" className="rounded-xl" data-testid="staff-name" /></F>
            <F label="Email Address"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" className="rounded-xl" data-testid="staff-email" /></F>
            <F label="4-Digit PIN">
              <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="e.g. 5821" maxLength={4} inputMode="numeric" className="rounded-xl w-32 font-mono text-xl tracking-widest" data-testid="staff-pin" />
            </F>
          </div>
          <DialogFooter>
            <Button onClick={addStaff} className="rounded-full font-display bg-primary hover:bg-primary/90" data-testid="staff-save">Add Staff Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <PinConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Remove Staff Member?"
        description={confirmDel ? `Remove ${confirmDel.name}? They will no longer be able to log in to the portal.` : ""}
        onConfirm={() => removeStaff(confirmDel)}
        confirmLabel="Remove"
        testid="staff-confirm-delete"
      />
    </Panel>
  );
}

const DetailSection = ({ title, children }) => (
  <div>
    <h4 className="font-display text-lg text-primary border-b border-border pb-1 mb-3">{title}</h4>
    <div className="space-y-2">{children}</div>
  </div>
);

const DetailRow = ({ label, children, testid }) => (
  <div className="grid grid-cols-5 gap-2 text-sm" data-testid={testid}>
    <span className="col-span-2 text-slate-500 font-medium">{label}</span>
    <span className="col-span-3 text-slate-800 break-words">{children}</span>
  </div>
);


const ProfileRow = ({ label, children, testid }) => (
  <div className="flex gap-2 text-sm" data-testid={testid}>
    <span className="text-slate-500 w-24 shrink-0">{label}</span>
    <span className="text-slate-800 font-medium">{children}</span>
  </div>
);
